import { useLayoutEffect, type RefObject } from "react";

const SCROLL_REGION_SELECTOR = "[data-dashboard-mobile-scroll-region]";
const VAR_NAME = "--ds-dashboard-mobile-tabs-height";
const NARROW_QUERY = "(max-width: 1023px)";

/**
 * Publishes the live mobile tab strip height on the dashboard scroll region so
 * scroll-padding and height calcs can use `var(--ds-dashboard-mobile-tabs-height)`.
 */
export function useDashboardMobileTabsHeightVar(
  tabsRef: RefObject<HTMLElement | null>,
): void {
  useLayoutEffect(() => {
    const tabs = tabsRef.current;
    const region = document.querySelector<HTMLElement>(SCROLL_REGION_SELECTOR);
    if (!tabs || !region) return;

    const mq = window.matchMedia(NARROW_QUERY);

    const apply = () => {
      if (!mq.matches) {
        region.style.removeProperty(VAR_NAME);
        return;
      }
      const rect = tabs.getBoundingClientRect();
      const h = rect.height > 0 && rect.width > 0 ? Math.ceil(rect.height) : 0;
      region.style.setProperty(VAR_NAME, `${h}px`);
    };

    apply();

    const ro = new ResizeObserver(apply);
    ro.observe(tabs);
    mq.addEventListener("change", apply);

    return () => {
      ro.disconnect();
      mq.removeEventListener("change", apply);
      region.style.removeProperty(VAR_NAME);
    };
  }, [tabsRef]);
}
